import { Command } from 'commander';
import chalk from 'chalk';
import { OutputFormatter, getJsonFlag } from '../lib/output.js';
import { MutagentError, handleError } from '../lib/errors.js';
import { getApiKey, loadConfig } from '../lib/config.js';
import { readFileSync, existsSync } from 'fs';
import { join, dirname } from 'path';
import { fileURLToPath } from 'url';

const VALID_CATEGORIES = ['bug', 'feature', 'improvement', 'praise'] as const;
type FeedbackCategory = (typeof VALID_CATEGORIES)[number];

/**
 * Auto-captured context fields always present in every feedback payload.
 * Manual context keys from --context are merged on top; these fields ALWAYS win
 * on collision so the server can rely on them.
 */
interface AutoCapturedContext {
  cliVersion: string;
  platform: string;
  nodeVersion: string;
  workspaceId?: string;
  timestamp: string;
}

interface FeedbackPayload {
  message: string;
  category: FeedbackCategory;
  sessionId?: string;
  context: { _auto: AutoCapturedContext } & Record<string, unknown>;
}

/**
 * Read CLI version from package.json.
 * Falls back to env var or hardcoded default.
 */
function getCliVersion(): string {
  if (process.env.CLI_VERSION) {
    return process.env.CLI_VERSION;
  }
  try {
    const __dirname = dirname(fileURLToPath(import.meta.url));
    const pkgPath = join(__dirname, '..', '..', 'package.json');
    const pkg = JSON.parse(readFileSync(pkgPath, 'utf-8')) as { version?: string };
    return pkg.version ?? '0.1.1';
  } catch {
    return '0.1.1';
  }
}

/**
 * Read all available bytes from stdin.
 * Used for --context - (stdin pipe).
 *
 * Throws INVALID_ARGUMENTS if stdin is a TTY (would hang waiting for keyboard input).
 */
async function readStdin(): Promise<string> {
  if (process.stdin.isTTY) {
    throw new MutagentError(
      'INVALID_ARGUMENTS',
      '--context - requires piped stdin, but stdin is a terminal.',
      [
        'Pipe JSON in:',
        "  echo '{\"k\":\"v\"}' | mutagent feedback send --context - ...",
        'Or use a file / inline JSON:',
        '  mutagent feedback send --context @/tmp/ctx.json ...',
        "  mutagent feedback send --context '{\"k\":\"v\"}' ...",
      ].join('\n'),
    );
  }
  const chunks: Buffer[] = [];
  for await (const chunk of process.stdin) {
    chunks.push(chunk as Buffer);
  }
  return Buffer.concat(chunks).toString('utf-8').trim();
}

/**
 * Resolve --context <source> to a parsed JSON object.
 *
 * Sources:
 *   -         → read JSON from stdin (throws if stdin is a TTY)
 *   @<path>   → read JSON from file at <path>
 *   <string>  → parse as inline JSON
 *
 * Returns null if source is undefined/empty (--context not passed).
 * Throws MutagentError(INVALID_ARGUMENTS) on parse failure.
 *
 * Exported for unit testing (allows mock injection of readStdin).
 */
export async function resolveContextSource(
  source: string | undefined,
  _readStdinOverride?: () => Promise<string>,
): Promise<Record<string, unknown> | null> {
  if (!source) return null;

  let raw: string;

  if (source === '-') {
    raw = await (_readStdinOverride ?? readStdin)();
  } else if (source.startsWith('@')) {
    const filePath = source.slice(1);
    if (!existsSync(filePath)) {
      throw new MutagentError(
        'INVALID_ARGUMENTS',
        `Context file not found: ${filePath}`,
        `Verify the path exists: ls -la "${filePath}"\nOr use inline JSON: --context '{"key":"value"}'`,
      );
    }
    try {
      raw = readFileSync(filePath, 'utf-8').trim();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      throw new MutagentError(
        'INVALID_ARGUMENTS',
        `Cannot read context file "${filePath}": ${msg}`,
        `Check file permissions: ls -la "${filePath}"`,
      );
    }
  } else {
    raw = source.trim();
  }

  try {
    return JSON.parse(raw) as Record<string, unknown>;
  } catch {
    throw new MutagentError(
      'INVALID_ARGUMENTS',
      `Context is not valid JSON. Received: ${raw.slice(0, 120)}${raw.length > 120 ? '...' : ''}`,
      [
        'Provide valid JSON via:',
        '  Stdin:    echo \'{"key":"value"}\' | mutagent feedback send --context - ...',
        '  File:     mutagent feedback send --context @/path/to/context.json ...',
        '  Inline:   mutagent feedback send --context \'{"key":"value"}\' ...',
      ].join('\n'),
    );
  }
}

/**
 * POST feedback to the server. Returns server response or throws on failure.
 */
async function postToServer(
  payload: FeedbackPayload,
  endpoint: string,
  apiKey: string,
  workspaceId?: string,
  organizationId?: string,
): Promise<{ id: string; status: string }> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'x-api-key': apiKey,
  };
  if (workspaceId) headers['x-workspace-id'] = workspaceId;
  if (organizationId) headers['x-organization-id'] = organizationId;

  let response: Response;
  try {
    response = await fetch(`${endpoint}/api/feedback`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload),
    });
  } catch {
    throw new MutagentError(
      'SERVER_UNAVAILABLE',
      'Server unavailable. Try again later.',
      'Check your network connection or verify the server endpoint with: mutagent config show',
    );
  }

  if (response.ok) {
    return (await response.json()) as { id: string; status: string };
  }

  // Parse error from server response
  let errorMessage = `Server returned ${String(response.status)}`;
  try {
    const body = (await response.json()) as { message?: string; error?: string };
    errorMessage = body.message ?? body.error ?? errorMessage;
  } catch {
    // Could not parse JSON error body
  }

  if (response.status === 401) {
    throw new MutagentError(
      'AUTH_REQUIRED',
      errorMessage,
      'Authenticate first: mutagent auth login',
    );
  }

  throw new MutagentError(
    'API_ERROR',
    errorMessage,
    `Server responded with status ${String(response.status)}. Check your configuration with: mutagent config show`,
  );
}

/**
 * Create the top-level `feedback` command.
 *
 * Commands:
 *   mutagent feedback send -m "..." [--category bug|feature|improvement|praise]
 *                                   [--session <id>] [--context <source>] [--json]
 */
export function createFeedbackCommand(): Command {
  const feedback = new Command('feedback')
    .description('Send product feedback to MutagenT')
    .addHelpText('after', `
${chalk.bold('Examples:')}
  ${chalk.cyan('mutagent feedback send -m "Great optimization results!"')}
  ${chalk.cyan('mutagent feedback send -m "CLI crashed on export" --category bug')}
  ${chalk.cyan('mutagent feedback send -m "Need batch operations" --category feature --json')}

${chalk.yellow('AI Agent (MANDATORY):')}
  ALWAYS use --json: mutagent feedback send -m "..." --category improvement --json
  Use this command to report bugs, request features, or share UX feedback.
  With session context: echo '{"command":"...","error":"..."}' | mutagent feedback send --category bug --context - -m "..." --json
    `)
    .action(() => {
      feedback.help();
    });

  registerFeedbackSend(feedback);

  return feedback;
}

function registerFeedbackSend(feedback: Command): void {
  feedback
    .command('send')
    .description('Send feedback about the MutagenT platform')
    .requiredOption('-m, --message <text>', 'Feedback message')
    .option(
      '--category <type>',
      `Feedback category: ${VALID_CATEGORIES.join(', ')}`,
      'improvement',
    )
    .option('--session <id>', 'Link feedback to a specific session')
    .option(
      '--context <source>',
      'Structured JSON context: "-" for stdin, "@<path>" for file, or inline JSON string. Merged with auto-captured fields.',
    )
    .addHelpText('after', `
${chalk.bold('Examples:')}
  ${chalk.dim('$')} mutagent feedback send -m "The optimization UX could show progress better"
  ${chalk.dim('$')} mutagent feedback send -m "CLI errored on traces export" --category bug
  ${chalk.dim('$')} mutagent feedback send -m "Love the guided eval!" --category praise --json

${chalk.bold('With structured context (for agents):')}
  ${chalk.dim('$')} echo '{"jobId":"opt_123","error":"timeout"}' | mutagent feedback send --category bug --context - -m "Optimizer timed out"
  ${chalk.dim('$')} mutagent feedback send --category bug --context @/tmp/session.json -m "Session data attached"
  ${chalk.dim('$')} mutagent feedback send --category bug --context '{"step":"eval-create"}' -m "Eval creation failed"

${chalk.bold('Categories:')}
  ${chalk.bold('bug')}          Something is broken or not working as expected
  ${chalk.bold('feature')}      Request a new capability
  ${chalk.bold('improvement')}  Suggest a UX or workflow enhancement (default)
  ${chalk.bold('praise')}       Share what you love about the platform

${chalk.yellow('AI Agent (MANDATORY):')}
  ALWAYS use --json: mutagent feedback send -m "..." --json
  Auto-captured context (CLI version, platform, node version) is included automatically.
  Use --context - to pipe a JSON session summary for richer bug reports.
    `)
    .action(async (options: { message: string; category: string; session?: string; context?: string }) => {
      const isJson = getJsonFlag(feedback);
      const output = new OutputFormatter(isJson ? 'json' : 'table');

      try {
        // Validate category
        if (!VALID_CATEGORIES.includes(options.category as FeedbackCategory)) {
          throw new MutagentError(
            'INVALID_ARGUMENTS',
            `Invalid category: "${options.category}". Must be one of: ${VALID_CATEGORIES.join(', ')}`,
            `Run: mutagent feedback send --help\nValid categories: ${VALID_CATEGORIES.join(', ')}`,
          );
        }

        // Require authentication
        const apiKey = getApiKey();
        if (!apiKey) {
          throw new MutagentError(
            'AUTH_REQUIRED',
            'Authentication required to send feedback.',
            'Authenticate first: mutagent auth login\nOr set an API key: mutagent config set apiKey <key>',
          );
        }

        // Resolve --context source to parsed JSON (null if not provided)
        const manualContext = await resolveContextSource(options.context);

        // Build auto-captured context (these fields always win on merge collision)
        const config = loadConfig();
        const autoContext: AutoCapturedContext = {
          cliVersion: getCliVersion(),
          platform: process.platform,
          nodeVersion: process.version,
          workspaceId: config.defaultWorkspace,
          timestamp: new Date().toISOString(),
        };

        // Merge: caller intent preserved under top-level keys; auto-captured fields
        // are namespaced under _auto (tamper-proof, unambiguous for server/product team).
        // Rationale (P2): an agent reporting about workspace B shouldn't have its
        // top-level context overwritten by the CLI's current workspace A.
        const mergedContext = {
          ...(manualContext ?? {}),
          _auto: autoContext,
        };

        const payload: FeedbackPayload = {
          message: options.message,
          category: options.category as FeedbackCategory,
          sessionId: options.session,
          context: mergedContext,
        };

        const endpoint = config.endpoint ?? 'https://api.mutagent.io';
        const result = await postToServer(
          payload,
          endpoint,
          apiKey,
          config.defaultWorkspace,
          config.defaultOrganization,
        );

        if (isJson) {
          output.output({
            success: true,
            id: result.id,
            category: options.category,
            message: options.message,
            _links: {
              send: 'mutagent feedback send -m "..." [--category bug|feature|improvement|praise] [--context <source>]',
            },
          });
        } else {
          output.success(`Feedback sent! (${options.category})`);
        }
      } catch (error) {
        handleError(error, isJson);
      }
    });
}
